/**
 * 트리아지 데이터 매핑 상수
 * @source medicrew-renewal-front-end/src/app/pages/session/service/study-result.service.ts
 */

/** 상처 종류 매핑 (injury) */
export const INJURY_TYPE_MAP: Record<number, { ko: string; en: string }> = {
  1: { ko: '찰과상', en: 'Abrasion' },
  2: { ko: '타박상', en: 'Contusion' },
  3: { ko: '자상', en: 'Puncture' },
  4: { ko: '열상', en: 'Laceration' },
  5: { ko: '박리', en: 'Avulsion' },
  6: { ko: '관통상', en: 'Penetration' },
  7: { ko: '화상', en: 'Burn' },
  8: { ko: '혈종', en: 'Hematoma' },
  9: { ko: '이물질', en: 'Foreign body' },
  10: { ko: '흡인', en: 'Inhalation' },
  11: { ko: '염좌', en: 'Sprain' },
  12: { ko: '좌상', en: 'Strain' },
  13: { ko: '골절', en: 'Fracture' },
  14: { ko: '압궤', en: 'Crush' },
  15: { ko: '탈구', en: 'Dislocation' },
  16: { ko: '수포', en: 'Vesicle' },
  17: { ko: '부종', en: 'Edema' },
  18: { ko: '절단', en: 'Amputation' },
};

/** 중증도 매핑 (degree) */
export const SEVERITY_LEVEL_MAP: Record<number, { ko: string; en: string }> = {
  1: { ko: '경미한', en: 'Mild' },
  2: { ko: '중등도', en: 'Moderate' },
  3: { ko: '중증', en: 'Severe' },
  4: { ko: '중증', en: 'Severe' },
  5: { ko: '중증', en: 'Severe' },
  6: { ko: '사망가능한', en: 'life-threatening' },
};

/** 주호소 (Chief Complaint) 매핑 */
export const SYMPTOM_MAP: Record<number, { ko: string; en: string }> = {
  1: { ko: '통증', en: 'Pain' },
  2: { ko: '골절', en: 'Fracture' },
  3: { ko: '탈구', en: 'Dislocation' },
  4: { ko: '염좌', en: 'Sprain' },
  5: { ko: '열상', en: 'Laceration' },
  6: { ko: '찰과상', en: 'Abrasion' },
  7: { ko: '타박상', en: 'Contusion' },
  8: { ko: '절단', en: 'Amputation' },
  9: { ko: '압궤손상', en: 'Crush injury' },
  10: { ko: '화상', en: 'Burn' },
  11: { ko: '의식장애', en: 'Altered consciousness' },
  12: { ko: '기도 이물', en: 'Airway obstruction' },
  13: { ko: '호흡곤란', en: 'Dyspnea' },
  14: { ko: '호흡정지', en: 'Respiratory arrest' },
  15: { ko: '심계항진', en: 'Palpitations' },
  16: { ko: '심정지', en: 'Cardiac arrest' },
  17: { ko: '경련', en: 'Seizure' },
  18: { ko: '오심/구토', en: 'Nausea/Vomiting' },
  19: { ko: '객혈', en: 'Hemoptysis' },
  20: { ko: '토혈', en: 'Hematemesis' },
  21: { ko: '비출혈', en: 'Nasal bleeding' },
  22: { ko: '출혈', en: 'Bleeding' },
  23: { ko: '고열', en: 'High fever' },
  24: { ko: '저체온', en: 'Hypothermia' },
  25: { ko: '어지러움', en: 'Dizziness' },
  26: { ko: '편마비', en: 'Hemiplegia' },
  27: { ko: '사지마비', en: 'Quadriplegia' },
  28: { ko: '이물질', en: 'Foreign body' },
};

/**
 * 상처 정보를 포맷팅
 */
export function formatInjury(injury: number, degree: number, lang: 'ko' | 'en' = 'ko'): string {
  const injuryText = INJURY_TYPE_MAP[injury]?.[lang] || INJURY_TYPE_MAP[injury]?.ko || '';
  const degreeText = SEVERITY_LEVEL_MAP[degree]?.[lang] || SEVERITY_LEVEL_MAP[degree]?.ko || '';
  return `${degreeText} ${injuryText}`.trim();
}

/**
 * 주호소를 포맷팅
 */
export function formatSymptom(cc: number, lang: 'ko' | 'en' = 'ko'): string {
  const symptom = SYMPTOM_MAP[cc];
  return symptom ? symptom[lang] || symptom.ko : (lang === 'en' ? 'None' : '없음');
}

/**
 * 중재 여부를 텍스트로 변환
 * @param aidHistory - aid_history 배열 또는 불린값
 */
export function formatIntervention(aidHistory: any[] | boolean | undefined, lang: 'ko' | 'en' = 'ko'): string {
  let isAirway = false;
  
  if (typeof aidHistory === 'boolean') {
    isAirway = aidHistory;
  } else if (Array.isArray(aidHistory)) {
    isAirway = aidHistory.some((aid: any) => aid.aid_id === 'aLAa');
  }
  
  if (lang === 'en') {
    return isAirway ? 'Airway Opening' : 'Not Performed';
  }
  return isAirway ? '기도개방' : '미수행';
}

/**
 * 성별을 텍스트로 변환
 * @param gender - boolean (true=남성, false=여성) 또는 문자열
 */
export function formatGender(gender: unknown, lang: 'ko' | 'en' = 'ko'): string {
  if (typeof gender === 'boolean') {
    if (lang === 'en') {
      return gender ? 'Male' : 'Female';
    }
    return gender ? '남' : '여';
  }
  if (typeof gender === 'string') {
    const normalized = gender.toLowerCase().trim();
    // 문자열 "true", "false" 처리
    if (normalized === 'true' || normalized === '1') {
      return lang === 'en' ? 'Male' : '남';
    }
    if (normalized === 'false' || normalized === '0') {
      return lang === 'en' ? 'Female' : '여';
    }
    // 일반 문자열 처리
    if (normalized === 'male' || normalized === 'm' || normalized === '남' || normalized === '남성') {
      return lang === 'en' ? 'Male' : '남';
    }
    if (normalized === 'female' || normalized === 'f' || normalized === '여' || normalized === '여성') {
      return lang === 'en' ? 'Female' : '여';
    }
  }
  return '—';
}
